import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { DeviceDetectorService } from 'ngx-device-detector';
import {
	AnayliticalLog,
	AnalyticalViewOptions,
} from 'src/app/models/apis/analytical-logs';
import { CmvHttpEntidadResponse } from 'src/app/models/apis/entities';
import { environment } from 'src/environments/environment';
import { HttpHeadersBuilder } from '../apis/utils/http-header-builder';
import { Store } from '@ngrx/store';
import { selectPreAplicationState } from 'src/app/state/selectors/pre-application.selectors';
import { take, zip } from 'rxjs';
import { Coords } from 'src/app/data/utils/geolocation.utils';
import { GeolocationService } from './geolocation.service';
import { selectCurrentProcessInfo } from 'src/app/state/selectors/onboarding-process.selectors';
import { selectIsGeolocationEnabled } from 'src/app/state/selectors/app-settings.selector';
import { OnboardingTokenService } from './onboarding-token.service';
import { LocalStorageService } from './local-storage.service';
import { CONTACT_MEAN } from 'src/app/data/onboarding-constants.data';

@Injectable({
	providedIn: 'root',
})
export class AnalyticalLogsService {
	private readonly baseUrl: string = `${environment.urlBase}/onboarding/`;
	constructor(
		private deviceService: DeviceDetectorService,
		private httpClient: HttpClient,
		private store: Store,
		private geolocationService: GeolocationService,
		private onboardingTokenService: OnboardingTokenService,
		private localStorageService: LocalStorageService
	) {}

	logAnalytics(analyticalViewOptions?: AnalyticalViewOptions): void {
		this.store
			.select(selectIsGeolocationEnabled)
			.pipe(take(1))
			.subscribe(isGeolocationEnabled => {
				if (isGeolocationEnabled) {
					this.sendLogsWithCoords(analyticalViewOptions);
					return;
				}
				this.sendLogsWithDefaultCoords(analyticalViewOptions);
			});
	}

	private sendLogsWithCoords(analyticalViewOptions: AnalyticalViewOptions) {
		this.geolocationService.getCoords().subscribe({
			next: (coords: Coords) => {
				const { latitude, longitude } = coords;
				this.getStateDataAndSendLog(analyticalViewOptions, {
					latitude,
					longitude,
				});
			},
			error: () => {
				this.sendLogsWithDefaultCoords(analyticalViewOptions);
			},
		});
	}

	private sendLogsWithDefaultCoords(
		analyticalViewOptions: AnalyticalViewOptions
	) {
		this.getStateDataAndSendLog(analyticalViewOptions, {
			latitude: 0,
			longitude: 0,
		});
	}

	private getStateDataAndSendLog(
		analyticalViewOptions: AnalyticalViewOptions,
		coords: Coords,
		errorMessage: string = ''
	): void {
		zip([
			this.store.select(selectPreAplicationState).pipe(take(1)),
			this.store.select(selectCurrentProcessInfo).pipe(take(1)),
		]).subscribe({
			next: results => {
				const [preApplicationState, currentProcessInfo] = results;
				const analyticalLog: AnayliticalLog = {
					...analyticalViewOptions,
					numero: preApplicationState.applicantId,
					codigo_producto_core: preApplicationState.productCobisId,
					navegador: this.deviceService.browser,
					so: this.deviceService.os,
					dispositivo: this.deviceService.device,
					movil: this.deviceService.isMobile(),
					localizacion_latitud: coords.latitude.toString(),
					localizacion_longitud: coords.longitude.toString(),
					exception_error: `${errorMessage}:${
						analyticalViewOptions?.exception_error ?? ''
					}`,
					fecha_alta: new Date().toISOString(),
					notas: analyticalViewOptions?.notas ?? '',
					memoria_ram: '',
					id_tipo_persona: preApplicationState.applicantType,
					id_producto: preApplicationState.productId,
					paso_proceso:
						analyticalViewOptions?.paso_proceso ??
						currentProcessInfo.currentStepNumber,
					titulo_pantalla:
						analyticalViewOptions?.titulo_pantalla ??
						currentProcessInfo.currentRouteName,
					web_token:
						this.onboardingTokenService.getOnboardingToken() ?? '',
					medio_de_contacto:
						this.localStorageService.get<number>(CONTACT_MEAN) ?? 0,
				};
				this.sendLog(analyticalLog);
			},
		});
	}

	private sendLog(analyticalLog: AnayliticalLog) {
		const headers: HttpHeaders = new HttpHeadersBuilder()
			.withClientId()
			.withUserAndPassword()
			.withChannelId()
			.getHeaders();

		this.httpClient
			.post<CmvHttpEntidadResponse<string>>(
				`${this.baseUrl}logDigitalAnalitica`,
				analyticalLog,
				{ headers }
			)
			.subscribe({
				next: (response: CmvHttpEntidadResponse<string>) => {
					console.log(
						'🚀 ~ file: analytical-logs.service.ts:72 ~ AnalyticalLogsService ~ sendLog ~ response:',
						response.Mensaje
					);
				},
				error: err => {
					console.log(
						'🚀 ~ file: analytical-logs.service.ts:77 ~ AnalyticalLogsService ~ sendLog ~ err:',
						err
					);
				},
			});
	}
}
